import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api, downloadFile, mutate, uploadFile } from '../../shared/api';
import { DateInput } from '../../shared/DateInput';
import { formatDate } from '../../shared/formatting';
import type { User } from '../../types/biApp';
import { EmployeePicker } from './EmployeePicker';

type Locale = 'hr' | 'en';

const BROWN = 'var(--ad-ink)';
const GOLD = 'var(--ad-acc)';

const card: React.CSSProperties = { background: '#fff', borderRadius: 14, padding: 18, boxShadow: '0 2px 10px rgba(107,79,28,0.10)', border: '1px solid #efe6d2' };
const btn = (active: boolean): React.CSSProperties => ({
    padding: '8px 16px', borderRadius: 999, border: '1px solid ' + (active ? BROWN : '#e0d6c0'),
    background: active ? BROWN : '#fff', color: active ? '#fff' : BROWN, cursor: 'pointer', fontWeight: 600, fontSize: '0.86rem',
});
const inp: React.CSSProperties = { padding: '8px 10px', borderRadius: 8, border: '1px solid #d8cdb2', fontSize: '0.86rem', width: '100%', boxSizing: 'border-box' };
const th: React.CSSProperties = { textAlign: 'left', padding: '8px 10px', fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: BROWN, borderBottom: '2px solid #efe6d2' };
const td: React.CSSProperties = { padding: '8px 10px', fontSize: '0.86rem', borderBottom: '1px solid #f3ecdb' };

type EmployeeLite = {
    id: number;
    first_name: string | null;
    last_name: string | null;
    company_name: string | null;
    department_name: string | null;
};

type DocRow = {
    id: number;
    public_id: string;
    doc_type: string;
    title: string;
    original_filename: string | null;
    mime: string | null;
    size_bytes: number | null;
    issue_date: string | null;
    expiry_date: string | null;
    created_at: string | null;
};

type Dossier = {
    employee: {
        id: number;
        first_name: string | null;
        last_name: string | null;
        oib: string | null;
        jmbag: string | null;
        employment_type: string;
        employment_status: string;
        hire_date: string | null;
        termination_date: string | null;
        contact_phone: string | null;
        contact_email: string | null;
        company_name: string | null;
        department_name: string | null;
        position_name: string | null;
        user_name: string | null;
        user_email: string | null;
        user_username: string | null;
    };
    branches: { id: number; department_name: string | null; is_primary: number; plaza_coefficient: number; allocation_pct: number; coefficient_locked: number }[];
    documents: DocRow[];
    education?: { completed_lessons: number; passed_exams: number; assigned: number; subjects: { title: string; score: number; passed_at: string | null }[] };
    circulars?: { total: number; opened: number; pct: number | null };
};

const DOC_TYPES: { value: string; hr: string; en: string }[] = [
    { value: 'ugovor', hr: 'Ugovor', en: 'Contract' },
    { value: 'certifikat_interni', hr: 'Certifikat (interni)', en: 'Certificate (internal)' },
    { value: 'certifikat_eksterni', hr: 'Certifikat (eksterni)', en: 'Certificate (external)' },
    { value: 'edukacija_interna', hr: 'Edukacija (interna)', en: 'Training (internal)' },
    { value: 'edukacija_eksterna', hr: 'Edukacija (eksterna)', en: 'Training (external)' },
    { value: 'nda', hr: 'NDA', en: 'NDA' },
    { value: 'ostalo', hr: 'Ostalo', en: 'Other' },
];

function daysUntil(iso: string | null): number | null {
    if (!iso) return null;
    const d = new Date(iso + 'T00:00:00');
    if (Number.isNaN(d.getTime())) return null;
    return Math.ceil((d.getTime() - Date.now()) / 86400000);
}

export function DossierTab({ token, locale, user }: { token: string; locale: Locale; user: User }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    void user;

    const [selId, setSelId] = useState<number | null>(null);

    return (
        <div style={{ display: 'grid', gap: 14 }}>
            <div style={card}>
                <label style={{ display: 'block', fontWeight: 600, color: BROWN, marginBottom: 6 }}>{t('Odaberi zaposlenika', 'Select employee')}</label>
                <EmployeePicker token={token} locale={locale} value={selId} onChange={setSelId} />
            </div>

            {selId !== null ? <DossierDetail key={selId} token={token} locale={locale} employeeId={selId} /> : null}
        </div>
    );
}

export function DossierDetail({ token, locale, employeeId }: { token: string; locale: Locale; employeeId: number }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [data, setData] = useState<Dossier | null>(null);
    const [err, setErr] = useState<string | null>(null);

    const load = useCallback(() => {
        setData(null); setErr(null);
        api<Dossier>(`/hr/employees/${employeeId}/documents`, token)
            .then(setData)
            .catch((e) => setErr(String(e?.message ?? e)));
    }, [token, employeeId]);
    useEffect(load, [load]);

    if (err) return <div style={card}><p className="error" style={{ fontSize: '0.82rem' }}>{err}</p></div>;
    if (data === null) return <div style={card}><p className="muted">{t('Učitavam dosje…', 'Loading dossier…')}</p></div>;

    const e = data.employee;
    const fullName = `${e.last_name ?? ''} ${e.first_name ?? ''}`.trim() || `#${e.id}`;

    return (
        <>
            <div style={card}>
                <h3 style={{ margin: '0 0 8px', color: BROWN }}>{fullName}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px 16px', fontSize: '0.85rem' }}>
                    <Field label={t('Firma', 'Company')} value={e.company_name} />
                    <Field label={t('Poslovnica', 'Branch')} value={e.department_name} />
                    <Field label={t('Radno mjesto', 'Position')} value={e.position_name} />
                    <Field label="OIB" value={e.oib} />
                    <Field label={t('Vrsta', 'Type')} value={e.employment_type} />
                    <Field label={t('Status', 'Status')} value={e.employment_status} />
                    <Field label={t('Datum zaposlenja', 'Hire date')} value={e.hire_date ? formatDate(e.hire_date, locale) : null} />
                    <Field label={t('Login', 'Login')} value={e.user_username || e.user_email} />
                    <Field label={t('Kontakt telefon', 'Phone')} value={e.contact_phone} />
                    <Field label={t('Kontakt e-mail', 'Email')} value={e.contact_email} />
                </div>
                {data.branches.length > 0 ? (
                    <div style={{ marginTop: 10, fontSize: '0.8rem', color: BROWN }}>
                        {t('Poslovnice', 'Branches')}: {data.branches.map((b) => `${b.department_name ?? '?'}${b.is_primary ? ' ★' : ''} (${Number(b.allocation_pct)}%)`).join(' · ')}
                    </div>
                ) : null}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12 }}>
                <div style={card}>
                    <strong style={{ color: BROWN }}>🎓 {t('Edukacije', 'Education')}</strong>
                    <div style={{ display: 'flex', gap: 18, margin: '10px 0', flexWrap: 'wrap' }}>
                        <Stat label={t('Položeni ispiti', 'Passed exams')} value={data.education?.passed_exams ?? 0} />
                        <Stat label={t('Završene lekcije', 'Completed lessons')} value={data.education?.completed_lessons ?? 0} />
                        <Stat label={t('Dodijeljeno', 'Assigned')} value={data.education?.assigned ?? 0} />
                    </div>
                    {(data.education?.subjects ?? []).length > 0 ? (
                        <div style={{ fontSize: '0.82rem', color: '#6b5f3a' }}>
                            {data.education!.subjects.map((s, i) => (
                                <div key={i} style={{ padding: '3px 0', borderTop: i ? '1px solid #f3ecdb' : undefined }}>
                                    ✅ {s.title} <span className="muted">· {s.score}%{s.passed_at ? ' · ' + formatDate(s.passed_at.slice(0, 10), locale) : ''}</span>
                                </div>
                            ))}
                        </div>
                    ) : <p className="muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>{t('Nema položenih edukacija.', 'No passed educations.')}</p>}
                </div>
                <div style={card}>
                    <strong style={{ color: BROWN }}>📣 {t('Okružnice', 'Circulars')}</strong>
                    <div style={{ margin: '10px 0' }}>
                        <Stat label={t('Pročitano', 'Read')} value={`${data.circulars?.opened ?? 0}/${data.circulars?.total ?? 0}`} />
                    </div>
                    {data.circulars && data.circulars.total > 0 ? (
                        <>
                            <div style={{ height: 10, borderRadius: 6, background: '#efe6d2', overflow: 'hidden' }}>
                                <div style={{ width: `${data.circulars.pct ?? 0}%`, height: '100%', background: (data.circulars.pct ?? 0) >= 80 ? '#3f7a3f' : (data.circulars.pct ?? 0) >= 50 ? GOLD : '#b14040' }} />
                            </div>
                            <div style={{ fontSize: '0.82rem', color: BROWN, marginTop: 6, fontWeight: 600 }}>{data.circulars.pct}% {t('pročitano', 'read')}</div>
                        </>
                    ) : <p className="muted" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>{t('Nema dodijeljenih okružnica.', 'No assigned circulars.')}</p>}
                </div>
            </div>

            <UploadForm token={token} locale={locale} employeeId={employeeId} onSaved={load} />

            <div style={card}>
                <strong style={{ color: BROWN }}>{t('Dokumenti', 'Documents')} ({data.documents.length})</strong>
                {data.documents.length === 0 ? (
                    <p className="muted" style={{ fontStyle: 'italic', marginTop: 8 }}>{t('Još nema dokumenata.', 'No documents yet.')}</p>
                ) : (
                    <div style={{ overflowX: 'auto', marginTop: 8 }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                            <thead><tr>
                                <th style={th}>{t('Naziv', 'Title')}</th>
                                <th style={th}>{t('Vrsta', 'Type')}</th>
                                <th style={th}>{t('Izdano', 'Issued')}</th>
                                <th style={th}>{t('Vrijedi do', 'Expires')}</th>
                                <th style={th}></th>
                            </tr></thead>
                            <tbody>
                                {data.documents.map((d) => (
                                    <DocRowView key={d.id} d={d} token={token} locale={locale} onChanged={load} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </>
    );
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
    return (
        <div>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9a8a55' }}>{label}</div>
            <div style={{ color: '#3a2f17' }}>{value || '—'}</div>
        </div>
    );
}

function Stat({ label, value }: { label: string; value: number | string }) {
    return (
        <div>
            <div style={{ fontSize: '1.4rem', fontWeight: 800, color: BROWN }}>{value}</div>
            <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.04em', color: '#9a8a55' }}>{label}</div>
        </div>
    );
}

function DocRowView({ d, token, locale, onChanged }: { d: DocRow; token: string; locale: Locale; onChanged: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [busy, setBusy] = useState(false);
    const typeLabel = DOC_TYPES.find((x) => x.value === d.doc_type);
    const days = daysUntil(d.expiry_date);
    const expiringSoon = days !== null && days < 60;

    const download = () => {
        const fname = d.original_filename || `${d.title}`;
        downloadFile(`/hr/documents/${d.id}/download`, token, fname).catch(() => undefined);
    };
    const remove = () => {
        if (!window.confirm(t('Obrisati dokument?', 'Delete document?'))) return;
        setBusy(true);
        mutate(`/hr/documents/${d.id}`, token, 'DELETE').then(onChanged).catch(() => setBusy(false));
    };

    return (
        <tr>
            <td style={td}>{d.title}{d.original_filename ? <div className="muted" style={{ fontSize: '0.72rem' }}>{d.original_filename}</div> : null}</td>
            <td style={td}>{typeLabel ? (locale === 'en' ? typeLabel.en : typeLabel.hr) : d.doc_type}</td>
            <td style={td}>{d.issue_date ? formatDate(d.issue_date, locale) : '—'}</td>
            <td style={td}>
                {d.expiry_date ? (
                    <span style={{ padding: '2px 8px', borderRadius: 999, fontSize: '0.74rem', background: expiringSoon ? '#f7e1e1' : '#eef3e8', color: expiringSoon ? '#b14040' : '#1f6e45', fontWeight: expiringSoon ? 700 : 400 }}>
                        {formatDate(d.expiry_date, locale)}{expiringSoon ? ` · ${days}${t(' d', ' d')}` : ''}
                    </span>
                ) : '—'}
            </td>
            <td style={{ ...td, whiteSpace: 'nowrap' }}>
                <button type="button" style={{ ...btn(false), padding: '4px 10px', marginRight: 6 }} onClick={download}>⬇ {t('Preuzmi', 'Download')}</button>
                <button type="button" style={{ ...btn(false), padding: '4px 10px', color: '#b14040', borderColor: '#e0c0c0' }} disabled={busy} onClick={remove}>✕</button>
            </td>
        </tr>
    );
}

function UploadForm({ token, locale, employeeId, onSaved }: { token: string; locale: Locale; employeeId: number; onSaved: () => void }) {
    const t = (hr: string, en: string) => (locale === 'en' ? en : hr);
    const [file, setFile] = useState<File | null>(null);
    const [docType, setDocType] = useState('ugovor');
    const [title, setTitle] = useState('');
    const [issueDate, setIssueDate] = useState('');
    const [expiryDate, setExpiryDate] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const reset = () => { setFile(null); setDocType('ugovor'); setTitle(''); setIssueDate(''); setExpiryDate(''); };

    const submit = () => {
        if (!file || !title) return;
        setErr(null); setBusy(true);
        const fd = new FormData();
        fd.append('file', file);
        fd.append('doc_type', docType);
        fd.append('title', title);
        if (issueDate) fd.append('issue_date', issueDate);
        if (expiryDate) fd.append('expiry_date', expiryDate);
        uploadFile(`/hr/employees/${employeeId}/documents`, token, fd)
            .then(() => { reset(); setBusy(false); onSaved(); })
            .catch((e) => { setErr(String(e?.message ?? e)); setBusy(false); });
    };

    const row: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 };
    return (
        <div style={{ ...card, borderColor: GOLD }}>
            <strong style={{ color: BROWN }}>＋ {t('Dodaj dokument', 'Add document')}</strong>
            {err ? <p className="error" style={{ fontSize: '0.82rem' }}>{err}</p> : null}
            <div style={{ marginTop: 10 }}>
                <label style={{ display: 'block', marginBottom: 10 }}>{t('Naziv dokumenta', 'Document title')}
                    <input style={inp} value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t('npr. Ugovor o radu 2026', 'e.g. Employment contract 2026')} />
                </label>
                <div style={row}>
                    <label>{t('Vrsta', 'Type')}
                        <select style={inp} value={docType} onChange={(e) => setDocType(e.target.value)}>
                            {DOC_TYPES.map((x) => <option key={x.value} value={x.value}>{locale === 'en' ? x.en : x.hr}</option>)}
                        </select>
                    </label>
                    <label>{t('Datoteka', 'File')}
                        <input style={inp} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
                    </label>
                </div>
                <div style={row}>
                    <label>{t('Datum izdavanja', 'Issue date')}
                        <DateInput value={issueDate} onChange={setIssueDate} />
                    </label>
                    <label>{t('Vrijedi do', 'Expiry date')}
                        <DateInput value={expiryDate} onChange={setExpiryDate} />
                    </label>
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="button" style={btn(true)} disabled={busy || !file || !title} onClick={submit}>{busy ? '…' : t('Učitaj', 'Upload')}</button>
                </div>
            </div>
        </div>
    );
}
